/**
 * Gestão de usuários — admin e gestor.
 *
 * O que cada um enxerga vem do servidor: o gestor simplesmente não recebe os
 * admins nem os outros gestores na listagem. A tela não filtra nada por conta
 * própria — esconder no front seria falsa segurança.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { mensagemErro } from '../api/axios';
import {
  ativarUsuario,
  atualizarUsuario,
  criarUsuario,
  inativarUsuario,
  listarUsuarios,
  perfisDisponiveis,
  resetarSenhaUsuario,
} from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Badge,
  Botao,
  Busca,
  LoadingSpinner,
  PageHeader,
  Paginacao,
  Painel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { useAuth } from '../contexts/AuthContext';
import { useOrdenacao } from '../hooks/useOrdenacao';
import { usePaginacao } from '../hooks/usePaginacao';
import { formatDate } from '../utils/formato';
import { PERFIL } from '../utils/opcoes';

/* ── Modal de cadastro / edição ─────────────────────────────────────────── */
function FormUsuario({ usuario, perfis, aoFechar, aoSalvar }) {
  const editando = Boolean(usuario);
  const [nome, setNome] = useState(usuario?.nome || '');
  const [email, setEmail] = useState(usuario?.email || '');
  const [senha, setSenha] = useState('');
  const [perfil, setPerfil] = useState(usuario?.perfil || perfis[0]?.valor || 'operador');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Ao editar, o perfil atual pode não estar entre os atribuíveis (um gestor
  // editando a si mesmo, por exemplo). Mostra, mas não deixa trocar.
  const perfilForaDaLista = editando && !perfis.some((p) => p.valor === perfil);

  const submeter = async (e) => {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      if (editando) {
        const dados = { nome, email };
        if (!perfilForaDaLista && perfil !== usuario.perfil) dados.perfil = perfil;
        await atualizarUsuario(usuario.id_usuario_portal, dados);
      } else {
        await criarUsuario({ nome, email, senha, perfil });
      }
      aoSalvar();
    } catch (err) {
      setErro(mensagemErro(err, 'Não foi possível salvar.'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ background: 'rgba(16,24,40,.5)' }} />
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <form
            className="modal-content"
            style={{ borderRadius: 16, border: '1px solid var(--ff-border)' }}
            onSubmit={submeter}
          >
            <div className="modal-header" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <h2 className="ff-painel__titulo">
                {editando ? 'Editar usuário' : 'Novo usuário'}
              </h2>
              <button type="button" className="btn-close" onClick={aoFechar} aria-label="Fechar" />
            </div>

            <div className="modal-body d-flex flex-column gap-3">
              <Alerta tipo="erro">{erro}</Alerta>

              <div className="ff-campo">
                <label htmlFor="u-nome">Nome</label>
                <input
                  id="u-nome"
                  className="ff-input"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                  minLength={2}
                  autoFocus
                />
              </div>

              <div className="ff-campo">
                <label htmlFor="u-email">E-mail</label>
                <input
                  id="u-email"
                  type="email"
                  className="ff-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {!editando && (
                <div className="ff-campo">
                  <label htmlFor="u-senha">Senha inicial</label>
                  <input
                    id="u-senha"
                    type="text"
                    className="ff-input ff-input--valor"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    required
                    minLength={6}
                    placeholder="mínimo 6 caracteres"
                  />
                  <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
                    Combine com a pessoa para que ela troque no primeiro acesso.
                  </span>
                </div>
              )}

              <div className="ff-campo">
                <label htmlFor="u-perfil">Perfil</label>
                <select
                  id="u-perfil"
                  className="ff-select"
                  value={perfil}
                  onChange={(e) => setPerfil(e.target.value)}
                  disabled={perfilForaDaLista}
                >
                  {perfilForaDaLista && (
                    <option value={perfil}>{PERFIL[perfil]?.label || perfil}</option>
                  )}
                  {perfis.map((p) => (
                    <option key={p.valor} value={p.valor}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
                  {perfilForaDaLista
                    ? 'Você não pode alterar este perfil.'
                    : PERFIL[perfil]?.descricao}
                </span>
              </div>
            </div>

            <div className="modal-footer" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <Botao variante="secundario" onClick={aoFechar} disabled={salvando}>
                Cancelar
              </Botao>
              <button type="submit" className="ff-btn ff-btn--primario" disabled={salvando}>
                {salvando ? 'Salvando…' : editando ? 'Salvar' : 'Criar usuário'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

/* ── Modal de reset de senha ────────────────────────────────────────────── */
function FormReset({ usuario, aoFechar, aoSalvar }) {
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const submeter = async (e) => {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      await resetarSenhaUsuario(usuario.id_usuario_portal, senha);
      aoSalvar(`Senha de ${usuario.nome} redefinida. As sessões abertas foram encerradas.`);
    } catch (err) {
      setErro(mensagemErro(err, 'Não foi possível redefinir a senha.'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ background: 'rgba(16,24,40,.5)' }} />
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <form
            className="modal-content"
            style={{ borderRadius: 16, border: '1px solid var(--ff-border)' }}
            onSubmit={submeter}
          >
            <div className="modal-header" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <div>
                <h2 className="ff-painel__titulo">Redefinir senha</h2>
                <p className="ff-painel__sub">
                  {usuario.nome} · {usuario.email}
                </p>
              </div>
              <button type="button" className="btn-close" onClick={aoFechar} aria-label="Fechar" />
            </div>

            <div className="modal-body d-flex flex-column gap-3">
              <Alerta tipo="erro">{erro}</Alerta>
              <div className="ff-aviso ff-aviso--atencao" style={{ margin: 0 }}>
                <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                <div>
                  Redefinir a senha <strong>encerra as sessões abertas</strong> desta pessoa.
                  Ela precisará entrar de novo com a nova senha.
                </div>
              </div>
              <div className="ff-campo">
                <label htmlFor="r-senha">Nova senha</label>
                <input
                  id="r-senha"
                  type="text"
                  className="ff-input ff-input--valor"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  required
                  minLength={6}
                  placeholder="mínimo 6 caracteres"
                  autoFocus
                />
              </div>
            </div>

            <div className="modal-footer" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <Botao variante="secundario" onClick={aoFechar} disabled={salvando}>
                Cancelar
              </Botao>
              <button type="submit" className="ff-btn ff-btn--primario" disabled={salvando}>
                {salvando ? 'Redefinindo…' : 'Redefinir senha'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

/* ── Página ─────────────────────────────────────────────────────────────── */
export default function Usuarios() {
  const { isAdmin } = useAuth();
  const [itens, setItens] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [busca, setBusca] = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(true);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [editando, setEditando] = useState(null);   // usuário ou 'novo'
  const [resetando, setResetando] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    Promise.all([listarUsuarios(), perfisDisponiveis()])
      .then(([u, p]) => {
        setItens(u.data);
        setPerfis(p.data);
      })
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar os usuários.')))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(carregar, [carregar]);

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return itens
      .filter((u) => (mostrarInativos ? true : u.ativo))
      .filter(
        (u) =>
          !t || u.nome?.toLowerCase().includes(t) || u.email?.toLowerCase().includes(t)
      );
  }, [itens, busca, mostrarInativos]);

  const ord = useOrdenacao(filtrados, 'nome', true);
  const pag = usePaginacao(ord.ordenados, 15);

  const alternarAtivo = async (u) => {
    const acao = u.ativo ? 'inativar' : 'reativar';
    if (!window.confirm(`Confirma ${acao} o acesso de ${u.nome}?`)) return;
    setErro('');
    try {
      await (u.ativo ? inativarUsuario : ativarUsuario)(u.id_usuario_portal);
      setAviso(`Acesso de ${u.nome} ${u.ativo ? 'inativado' : 'reativado'}.`);
      carregar();
    } catch (e) {
      setErro(mensagemErro(e, `Não foi possível ${acao} o usuário.`));
    }
  };

  const ativos = itens.filter((u) => u.ativo).length;

  return (
    <>
      <PageHeader
        titulo="Usuários"
        subtitulo={
          isAdmin
            ? 'Todos os usuários do sistema. Você pode criar administradores, gestores e operadores.'
            : 'Operadores sob sua gestão. Administradores e outros gestores não aparecem aqui.'
        }
        acoes={
          perfis.length > 0 && (
            <Botao variante="primario" icone="bi-person-plus" onClick={() => setEditando('novo')}>
              Novo usuário
            </Botao>
          )
        }
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>
      <Alerta tipo="ok" onFechar={() => setAviso('')}>
        {aviso}
      </Alerta>

      <Painel
        titulo="Contas"
        subtitulo={`${ativos} ativo${ativos === 1 ? '' : 's'} de ${itens.length}`}
        busca={
          itens.length > 0 && (
            <>
              <Busca
                valor={busca}
                onChange={setBusca}
                placeholder="Buscar nome ou e-mail"
                id="busca-usuarios"
              />
              <label className="ff-login-check" style={{ fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={mostrarInativos}
                  onChange={(e) => setMostrarInativos(e.target.checked)}
                />
                Mostrar inativos
              </label>
            </>
          )
        }
        rodape={filtrados.length > 0 && <Paginacao {...pag} rotulo="usuários" />}
      >
        {carregando ? (
          <LoadingSpinner />
        ) : filtrados.length === 0 ? (
          <Vazio
            icone="bi-people"
            titulo={itens.length === 0 ? 'Nenhum usuário' : 'Nenhum usuário corresponde à busca'}
          />
        ) : (
          <Tabela estreita>
            <thead>
              <tr>
                <Th campo="nome" estado={ord}>
                  Nome
                </Th>
                <Th campo="email" estado={ord}>
                  E-mail
                </Th>
                <Th campo="perfil" estado={ord}>
                  Perfil
                  <Ajuda titulo="Hierarquia de perfis">
                    <strong>Administrador</strong> faz tudo sobre todos e é o único que vê a
                    auditoria. <strong>Gestor</strong> gerencia os operadores.{' '}
                    <strong>Operador</strong> usa o sistema, sem gerenciar contas.
                  </Ajuda>
                </Th>
                <Th campo="ativo" estado={ord}>
                  Situação
                </Th>
                <Th campo="created_at" estado={ord} alinhar="right">
                  Criado em
                </Th>
                <Th alinhar="right">Ações</Th>
              </tr>
            </thead>
            <tbody>
              {pag.paginados.map((u) => {
                const cfg = PERFIL[u.perfil] || { label: u.perfil, tom: 'arquivado' };
                return (
                  <tr key={u.id_usuario_portal}>
                    <td className="col-principal">
                      {u.nome}
                      {u.eu_mesmo && (
                        <span style={{ color: 'var(--ff-muted-2)', fontWeight: 400 }}> (você)</span>
                      )}
                    </td>
                    <td className="col-meta">{u.email}</td>
                    <td>
                      <Badge tom={cfg.tom}>{cfg.label}</Badge>
                    </td>
                    <td>
                      {u.ativo ? (
                        <Badge tom="confirmado">Ativo</Badge>
                      ) : (
                        <Badge tom="reprovado">Inativo</Badge>
                      )}
                    </td>
                    <td className="col-data">{formatDate(u.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {u.pode_editar ? (
                        <div className="d-inline-flex gap-1">
                          <Botao
                            variante="fantasma"
                            pequeno
                            icone="bi-pencil"
                            onClick={() => setEditando(u)}
                            title="Editar cadastro"
                          >
                            Editar
                          </Botao>
                          <Botao
                            variante="fantasma"
                            pequeno
                            icone="bi-key"
                            onClick={() => setResetando(u)}
                            title="Redefinir senha"
                          >
                            Senha
                          </Botao>
                          {!u.eu_mesmo && (
                            <Botao
                              variante="fantasma"
                              pequeno
                              icone={u.ativo ? 'bi-person-slash' : 'bi-person-check'}
                              onClick={() => alternarAtivo(u)}
                              title={u.ativo ? 'Inativar acesso' : 'Reativar acesso'}
                            >
                              {u.ativo ? 'Inativar' : 'Reativar'}
                            </Botao>
                          )}
                        </div>
                      ) : (
                        <span className="col-meta">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Tabela>
        )}
      </Painel>

      {editando && (
        <FormUsuario
          usuario={editando === 'novo' ? null : editando}
          perfis={perfis}
          aoFechar={() => setEditando(null)}
          aoSalvar={() => {
            setEditando(null);
            setAviso('Usuário salvo.');
            carregar();
          }}
        />
      )}

      {resetando && (
        <FormReset
          usuario={resetando}
          aoFechar={() => setResetando(null)}
          aoSalvar={(msg) => {
            setResetando(null);
            setAviso(msg);
            carregar();
          }}
        />
      )}
    </>
  );
}
